import { Directive, ElementRef, HostBinding, HostListener, Input, OnChanges, OnDestroy } from '@angular/core';

interface MarkerLine { left: number; right: number; top: number; height: number; }

/** Paints behind the rendered text, without changing its content or line breaks. */
@Directive({ selector: '[appMarkerHint]' })
export class MarkerHintDirective implements OnChanges, OnDestroy {
  @Input() appMarkerHint = false;
  @HostBinding('class.marker-hint') readonly markerHint = true;
  private static nextId = 0;
  private readonly filterId = `marker-grain-${MarkerHintDirective.nextId++}`;
  private frame: number;
  private fadeTimer: ReturnType<typeof setTimeout>;
  private ink: SVGSVGElement | null = null;

  constructor(private element: ElementRef<HTMLElement>) {}

  ngOnChanges(): void {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.fadeTimer);
    if (this.appMarkerHint) {
      this.frame = requestAnimationFrame(() => this.paint());
    } else if (this.ink) {
      this.ink.style.opacity = '0';
      this.fadeTimer = setTimeout(() => this.clearInk(), 280);
    }
  }

  @HostListener('window:resize') onResize(): void {
    if (this.appMarkerHint) this.ngOnChanges();
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.fadeTimer);
    this.clearInk();
  }

  private clearInk(): void {
    this.ink?.remove();
    this.ink = null;
  }

  private paint(): void {
    this.clearInk();
    const host = this.element.nativeElement;
    const bounds = host.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const document = host.ownerDocument;
    const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    const lines: MarkerLine[] = [];
    let node: Node | null;

    // Character ranges follow actual wrapping, including the inline Pro button.
    while ((node = walker.nextNode())) {
      for (let index = 0; index < node.textContent.length; index++) {
        if (!node.textContent[index].trim()) continue;
        range.setStart(node, index);
        range.setEnd(node, index + 1);
        const rect = range.getBoundingClientRect();
        if (!rect.width || !rect.height) continue;
        const top = rect.top - bounds.top;
        let line = lines.find(item => Math.abs(item.top - top) < rect.height * .4);
        if (!line) {
          line = { left: rect.left - bounds.left, right: rect.right - bounds.left, top, height: rect.height };
          lines.push(line);
        } else {
          line.left = Math.min(line.left, rect.left - bounds.left);
          line.right = Math.max(line.right, rect.right - bounds.left);
          line.height = Math.max(line.height, rect.height);
        }
      }
    }
    range.detach();
    if (!lines.length) return;
    lines.sort((a, b) => a.top - b.top);

    const svgElement = (tag: string, attributes: { [key: string]: string }) => {
      const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      return element;
    };
    const svg = svgElement('svg', {
      'aria-hidden': 'true', focusable: 'false',
      viewBox: `0 0 ${bounds.width} ${bounds.height}`,
      width: String(bounds.width), height: String(bounds.height)
    }) as SVGSVGElement;
    svg.style.cssText = 'position:absolute;inset:0;overflow:visible;pointer-events:none;z-index:-1;transition:opacity 260ms ease;';
    const defs = svgElement('defs', {});
    const filter = svgElement('filter', { id: this.filterId, x: '-5%', y: '-100%', width: '110%', height: '300%' });
    filter.appendChild(svgElement('feTurbulence', { type: 'fractalNoise', baseFrequency: '.7 .12', numOctaves: '2', seed: '8', result: 'noise' }));
    filter.appendChild(svgElement('feDisplacementMap', { in: 'SourceGraphic', in2: 'noise', scale: '1.2', xChannelSelector: 'R', yChannelSelector: 'G', result: 'rough-ink' }));
    filter.appendChild(svgElement('feColorMatrix', { in: 'noise', type: 'matrix', values: '0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  .65 0 0 0 .35', result: 'grain' }));
    filter.appendChild(svgElement('feComposite', { in: 'rough-ink', in2: 'grain', operator: 'in' }));
    defs.appendChild(filter);
    svg.appendChild(defs);
    const totalWidth = lines.reduce((total, line) => total + line.right - line.left, 0);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let delay = 0;
    lines.forEach((line, index) => {
      const left = line.left - 2;
      const width = line.right - line.left + 4;
      const y = line.top + line.height * .64;
      const direction = index % 2 ? -1 : 1;
      const duration = Math.max(190, 780 * width / totalWidth);
      const group = svgElement('g', { filter: `url(#${this.filterId})` });
      const d = `M ${left} ${y} L ${left + width * .2} ${y - .65 * direction} L ${left + width * .58} ${y + .35 * direction} L ${left + width} ${y - .25 * direction}`;
      const layers = [
        { color: '#ffe500', width: line.height * .88, opacity: '.72', shift: 0 },
        { color: '#fff02b', width: line.height * .67, opacity: '.65', shift: -.7 },
        { color: '#efcf00', width: '.8', opacity: '.24', shift: line.height * .38 }
      ];
      layers.forEach(layer => {
        const path = svgElement('path', {
          d, fill: 'none', stroke: layer.color, 'stroke-width': String(layer.width),
          'stroke-opacity': layer.opacity, 'stroke-linecap': 'butt', 'stroke-linejoin': 'round',
          transform: `translate(0 ${layer.shift})`, pathLength: '1',
          'stroke-dasharray': '1', 'stroke-dashoffset': reducedMotion ? '0' : '1'
        });
        if (!reducedMotion) path.style.animation = `marker-ink-draw ${duration}ms cubic-bezier(.22,.55,.45,1) ${delay}ms both`;
        group.appendChild(path);
      });
      svg.appendChild(group);
      delay += duration + 90;
    });
    this.ink = svg;
    host.appendChild(svg);
  }
}
